import { useState, useEffect } from "react";
import { TIME_PERIODS } from "@/pages/warehouse/constants";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import {
  getDbtJobRunsList,
  getDbtJobRunsListGraphData,
  getDbtModelRunSummary,
} from "@/helpers/apis";
import { Stack } from "@altimateai/lego";
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@altimateai/lego";
import { DbtModel } from "@/types/dbtModels";
import { CommonTaskExecutionSummary } from "@/pages/query/common/tasks-details/CommonTaskExecutionSummary";
import { CommonTaskRunsGraph } from "@/pages/query/common/tasks-details/CommonTaskRunsGraph";
import { CommonTaskRunsTable } from "@/pages/query/common/tasks-details/CommonTaskRunsTable";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";

type DbtRunsProps = {
  model: DbtModel;
};

export const DbtRuns = ({ model }: DbtRunsProps) => {
  const [timePeriod, setTimePeriod] = useState(TIME_PERIODS[2].period);
  const [searchParams] = useSearchParams();
  const { setTotalPages, setTotalItems } = useTablePagination();

  const page = parseInt(searchParams.get("page") || "0", 10);
  const pageSize = parseInt(searchParams.get("pageSize") || "20", 10);
  const sortAttribute = searchParams.get("sortAttribute");
  const sortOrder = searchParams.get("sortOrder") as "asc" | "desc";
  const executionStatusFilter = searchParams.get("executionStatus")?.split(",");

  const getFilterParamsForModels = () => {
    return {
      page: page + 1,
      size: pageSize,
      sort_key: sortAttribute,
      sort_order: sortOrder,
      execution_status: executionStatusFilter,
      task_id: model.rk,
      startDate: dayjs().subtract(timePeriod, "day").toISOString(),
      endDate: dayjs().toISOString(),
    };
  };

  const { data: models, isLoading: isModelsLoading } = useQuery({
    queryKey: [
      "dbt-model-details",
      timePeriod,
      sortAttribute,
      sortOrder,
      executionStatusFilter,
      page,
      pageSize,
    ],
    enabled: !!model.project_rk,
    queryFn: () =>
      getDbtJobRunsList(encodeURIComponent(model.project_rk!), getFilterParamsForModels()),
  });

  // Handle successful models data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (models) {
      setTotalPages(models.pages ?? 0);
      setTotalItems(models.total ?? 0);
    }
  }, [models, setTotalPages, setTotalItems]);

  const { data: graphData, isLoading: isGraphDataLoading } = useQuery({
    queryKey: ["dbt-model-details-graph-data", timePeriod, executionStatusFilter],
    enabled: !!model.project_rk,
    queryFn: () => {
      return getDbtJobRunsListGraphData(encodeURIComponent(model.project_rk!), {
        sort_key: "start_time",
        sort_order: "asc",
        execution_status: executionStatusFilter,
        task_id: model.rk,
        startDate: dayjs().subtract(timePeriod, "day").toISOString(),
        endDate: dayjs().toISOString(),
      });
    },
  });

  const { data: summary, isLoading: isSummaryLoading } = useQuery({
    queryKey: ["dbt-summary", timePeriod],
    enabled: !!model.project_rk,
    queryFn: () =>
      getDbtModelRunSummary(encodeURIComponent(model.project_rk!), {
        task_id: model.rk,
        startDate: dayjs().subtract(timePeriod, "day").toISOString(),
        endDate: dayjs().toISOString(),
      }),
  });

  const handleChangeTimePeriod = (period: string) => {
    setTimePeriod(parseInt(period, 10));
  };

  return (
    <Stack direction={"column"} gap={"xl"} className={"al-p-1"}>
      <Stack gap={"lg"} className={"al-flex-row-reverse"}>
        <Stack direction={"row"} align={"center"} gap={"md"}>
          <Label className={"al-m-0"}>Time Period:</Label>
          <Select onValueChange={handleChangeTimePeriod} defaultValue={timePeriod.toString()}>
            <SelectTrigger className="al-w-[150px]">
              <SelectValue placeholder="Select a time period" />
            </SelectTrigger>
            <SelectContent>
              {TIME_PERIODS.map(period => {
                return (
                  <SelectItem key={period.label} value={period.period.toString()}>
                    {period.label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </Stack>
      </Stack>
      <CommonTaskExecutionSummary summary={summary} isLoading={isSummaryLoading} />
      <Stack direction="column" className={"al-p-4 al-border al-rounded-md"}>
        <CommonTaskRunsGraph graphData={graphData} isLoading={isGraphDataLoading} />
      </Stack>
      <Stack direction={"column"} gap={"md"} className={"al-mt-4"}>
        <CommonTaskRunsTable items={models?.items ?? []} isLoading={isModelsLoading} />
      </Stack>
    </Stack>
  );
};
